<template>
  <div class="local-body" :style="style">
    <!-- 펫 무대 + 하단 컨트롤을 한 div 아래로 묶음 -->
    <div class="home-stage-group">
      <!-- 펫 무대: 수평 중앙 + 하단에서 30vh 위. wrapper가 relative 기준 → 자식(프로필) absolute 배치 -->
      <div class="pet-stage">
        <!-- 펫 박스 좌측 위 아이콘 → 클릭 시 정보 다이얼로그(프로필/능력치 탭) -->
        <div class="profile-area">
          <button
            class="profile-avatar"
            :class="{ active: showInfo }"
            :style="{ background: petColor }"
            title="펫 정보"
            @click="openInfo(0)"
          >
            🐾
          </button>
        </div>

        <div class="pet-box">
          <!-- 상주 모델(modelHost)을 여기로 appendChild. 슬롯은 Vue가 관리하는 빈 컨테이너 -->
          <div ref="modelSlot" class="model-slot"></div>
        </div>
      </div>

      <!-- 홈 하단 컨트롤 패널 (시간대 슬라이더 + 밥주기) -->
      <home-control-panel :pet="myPet" v-model="hour" @fed="onFed" @played="onPlayed" />
    </div>

    <!-- 펫 정보 다이얼로그 (프로필 | 능력치 탭) -->
    <v-dialog v-model="showInfo" max-width="440">
      <v-card class="pet-info-card">
        <!-- 상단 고정 헤더 (사진·이름·레벨·경험치 + 닫기) — 탭과 무관하게 항상 표시 -->
        <pet-info-header v-if="showInfo" :pet-color="petColor" @close="showInfo = false" />
        <v-tabs v-model="infoTab" grow>
          <v-tab>프로필</v-tab>
          <v-tab>능력치</v-tab>
        </v-tabs>
        <v-window v-model="infoTab">
          <v-window-item>
            <v-card-text>
              <profile-panel v-if="showInfo" @change="onChangeProfile"></profile-panel>
            </v-card-text>
          </v-window-item>
          <v-window-item>
            <v-card-text>
              <stat-panel v-if="showInfo"></stat-panel>
            </v-card-text>
          </v-window-item>
        </v-window>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import BaseApi from "@/api/BaseApi";
import UserKeyMap from "../../domain/UserKeyMap";
import SidePanel from "./components/SidePanel.vue";
import EventExposure from "../../domain/EventExposure";

import ProfilePanel from "./components/ProfilePanel.vue";
import StatPanel from "./components/StatPanel.vue";
import PetInfoHeader from "./components/PetInfoHeader.vue";
import HomeControlPanel from "./components/HomeControlPanel.vue";

import { actionIdToLabel } from "@/filters/filter";
import SupabaseApi from "@/api/SupabaseApi";
import delay from "@/utils/delay";
import APP_CAMERA_POSITIONS from "@/constants/APP_CAMERA_POSITIONS";
import { MODEL_THUMB_COLOR } from "@/constants";
import MyPet from "@/entity/MyPet";
import * as modelHost from "@/domain/modelHost";
import { mapState } from "vuex";

// 시간대(0~24h)별 하늘색 키프레임 (밤=딥블루 / 일출·일몰=따뜻 / 낮=밝은 하늘)
const SKY_STOPS = [
  { h: 0, c: "#0b1026" },
  { h: 5, c: "#1c2340" },
  { h: 6.5, c: "#f6a06b" },
  { h: 8, c: "#cfe8ff" },
  { h: 12, c: "#bfe3ff" },
  { h: 16.5, c: "#d6e9ff" },
  { h: 18.5, c: "#f58e5b" },
  { h: 20, c: "#2c2747" },
  { h: 24, c: "#0b1026" },
];
// 시간대별 밝기(0~1) — 펫 컬러 톤(낮=선명 / 밤=어둡게)
const BRIGHT_STOPS = [
  { h: 0, v: 0.22 },
  { h: 5, v: 0.28 },
  { h: 7, v: 0.7 },
  { h: 9, v: 1 },
  { h: 16, v: 1 },
  { h: 18, v: 0.7 },
  { h: 20, v: 0.4 },
  { h: 22, v: 0.26 },
  { h: 24, v: 0.22 },
];

export default {
  components: { SidePanel, ProfilePanel, StatPanel, PetInfoHeader, HomeControlPanel },
  data() {
    return {
      isOnLoading: false,
      cameraButtons: [],
      animationButtons: [],

      value: 50,
      bufferValue: 100,
      petColor: "#ff8d4d", // 현재 펫 퍼스널 컬러
      myPet: null, // 내 버츄펫 도메인 엔티티(MyPet) — 상태/행동 응집
      showInfo: false, // 펫 정보 다이얼로그(프로필/능력치 탭)
      infoTab: 0, // 0=프로필 1=능력치
      hour: new Date().getHours(), // 시간대 슬라이더(0~24) — 일출/일몰 배경 효과
      style: {
        "background-color": "#f4f5fa",
      },

      // three
      modelIndex: null,
      cameraPosition: {},

      // handler
      keyDownHandler: () => {},

      // ModelFacade
      context: {
        streamerAliasId: "**",
        virtupetProfile: {},
      },
    };
  },
  computed: {
    ...mapState(["userName", "virtupetName"]),
  },
  watch: {
    // 컨트롤 패널에서 시간대 변경 시 배경 재계산
    hour() {
      this.applyBackground();
    },
  },
  async created() {},
  async mounted() {
    const modelIndex = await this.initModel();
    if (!modelIndex) return; // 펫 없음 → initModel이 펫 생성으로 라우팅 처리(아래 init 진행 안 함)

    this.myPet = await MyPet.load(); // 도메인 엔티티 로드(바이탈) — idle은 attach가 이미 시작
  },
  async destroyed() {
    EventExposure.removeAll(); // 제대로 제거가 안되면 두번씩 호출된다
    modelHost.detach(); // 상주 모델을 대기 홀더로 회수(다음 페이지에서 재사용)
  },

  methods: {
    changePrimaryColor(modelUrl) {
      // model_url(예: models/v6/fox.glb) → 카탈로그 id(fox) → 펫 퍼스널 컬러(MODEL_THUMB_COLOR)
      const id = (modelUrl || "")
        .split("/")
        .pop()
        .split("+")[0]
        .replace(/\.glb$/i, "")
        .replace(/_v\d+$/i, "")
        .toLowerCase();
      this.petColor = MODEL_THUMB_COLOR[id] || "#ff8d4d"; // 미정의 펫은 기본 주황 폴백
      this.style.primaryColor = this.petColor;
      this.applyBackground();
    },
    // 시간대(hour) 기반 배경: 위=하늘색(SKY_STOPS), 아래=펫 컬러를 시간 밝기로 톤다운
    applyBackground() {
      const sky = this._sampleColor(SKY_STOPS, this.hour);
      const bright = this._sampleScalar(BRIGHT_STOPS, this.hour); // 0~1
      const bottom = this._dim(this.petColor || "#ff8d4d", 0.3 + 0.7 * bright);
      this.style["background-image"] = `linear-gradient(${sky}, ${bottom})`;
      this.$forceUpdate();
    },
    // hex를 black 방향으로 f배(0~1) 어둡게.
    _dim(hex, f) {
      const [r, g, b] = this._hexToRgb(hex);
      return `rgb(${Math.round(r * f)}, ${Math.round(g * f)}, ${Math.round(b * f)})`;
    },
    _hexToRgb(hex) {
      const m = String(hex).replace("#", "").match(/.{2}/g);
      return m ? m.map((h) => parseInt(h, 16)) : [0, 0, 0];
    },
    // 키프레임 stops([{h,c(hex)}])에서 hour 보간 → rgb()
    _sampleColor(stops, h) {
      if (h <= stops[0].h) return stops[0].c;
      for (let i = 0; i < stops.length - 1; i++) {
        const a = stops[i];
        const b = stops[i + 1];
        if (h >= a.h && h <= b.h) {
          const t = (h - a.h) / (b.h - a.h);
          const ca = this._hexToRgb(a.c);
          const cb = this._hexToRgb(b.c);
          const mix = (k) => Math.round(ca[k] + (cb[k] - ca[k]) * t);
          return `rgb(${mix(0)}, ${mix(1)}, ${mix(2)})`;
        }
      }
      return stops[stops.length - 1].c;
    },
    // 키프레임 stops([{h,v(0~1)}])에서 hour 보간 → number
    _sampleScalar(stops, h) {
      if (h <= stops[0].h) return stops[0].v;
      for (let i = 0; i < stops.length - 1; i++) {
        const a = stops[i];
        const b = stops[i + 1];
        if (h >= a.h && h <= b.h) {
          const t = (h - a.h) / (b.h - a.h);
          return a.v + (b.v - a.v) * t;
        }
      }
      return stops[stops.length - 1].v;
    },
    onChangeProfile(virtupetProfile) {
      this.context.virtupetProfile = virtupetProfile;
    },
    openInfo(tab) {
      this.infoTab = tab;
      this.showInfo = true;
    },
    // 컨트롤 패널에서 밥을 먹였을 때: 펫 반응 애니메이션 + 사이드바 바이탈 갱신 + 토스트
    async onFed({ item, result }) {
      try {
        modelHost.api().executeInstruction("action-only--happy");
        await delay(1400);
        modelHost.api().startIdle("action-only--{{loop}}idle-40");
      } catch (e) {
        console.error("feed animation failed", e);
      }
      this.$store.commit("__bumpPetRevision"); // 사이드바 바이탈 즉시 갱신
      if (this.$notify) {
        if (result && result.bonus) {
          this.$notify({ type: "success", title: `🍀 행운! ${item.name} 효과 +50%`, duration: 2200 });
        } else {
          this.$notify({ type: "success", title: `${item.name} 냠냠!`, duration: 1800 });
        }
      }
    },
    // 놀아주기: 펫 반응 애니메이션 + 사이드바 갱신 + 토스트(내구도 소진/보너스 안내)
    async onPlayed({ item, result }) {
      try {
        modelHost.api().executeInstruction("action-only--agree");
        await delay(1400);
        modelHost.api().startIdle("action-only--{{loop}}idle-40");
      } catch (e) {
        console.error("play animation failed", e);
      }
      this.$store.commit("__bumpPetRevision");
      if (this.$notify) {
        if (result && result.bonus) {
          this.$notify({ type: "success", title: `🍀 행운! ${item.name} 기분 +50%`, duration: 2200 });
        } else if (result && result.broke) {
          this.$notify({ type: "warning", title: `${item.name}이(가) 다 닳았어요`, duration: 2000 });
        } else {
          this.$notify({ type: "success", title: `${item.name}으로 신나게 놀았어요!`, duration: 1800 });
        }
      }
    },
    async initModel() {
      const modelIndex = await SupabaseApi.PetProfile.fetchModelIndex();

      // 온보딩은 끝났지만 보유 펫이 없거나 선택된 모델이 없으면(=활성 펫의 model_url null)
      // 홈 렌더 대신 펫 선택 화면으로 유도(크래시 방지). PetSelect는 빈 슬롯("새 펫")을 항상 제공.
      if (!modelIndex) {
        this.$router.replace("/pet/select");
        return null;
      }

      // TODO: Extract
      this.changePrimaryColor(modelIndex);
      this.$forceUpdate();

      // 상주 모델 부착(슬롯 이동·init 1회·크기·카메라·idle을 modelHost가 일괄 책임)
      await modelHost.attach(this.$refs.modelSlot, {
        size: 400,
        modelIndex,
        camera: APP_CAMERA_POSITIONS.HOME_ZOOM,
      });
      return modelIndex;
    },
    async onAfterModelInit() {
      // await this._loadKeyboardFeature();
    },
    executeInstruction(...args) {
      modelHost.api().executeInstruction(...args);
    },
    async _loadKeyboardFeature() {
      // Load Default KeyMap
      const seedObj = await (async () => {
        const modelId = "asera-1234-1234-1234";
        const defaultKeyMap = await BaseApi.fetchModelDefaultKeyMap(modelId);
        if (!Object.isEmpty(defaultKeyMap)) return defaultKeyMap;

        return {};
      })();
      const userKeyMap = await UserKeyMap.create(seedObj);

      // class - ACTION OBJECT (Three.js에 소통하는 무언가. camera, animation, effect 등등)
      const cameraActions = [
        {
          actionId: "camera--restore",
          action: () => modelHost.api().restoreCamera(),
          shortcutKey: userKeyMap.getShortcutKey("camera--restore"),
        },
      ];

      const animationActions = modelHost.api().listAnimations().map(({ animationId }, i) => ({
        _animationId: animationId,
        actionId: `animation--${animationId}`,
        shortcutKey: userKeyMap.getShortcutKey(`animation--${animationId}`),
      }));

      const allActions = [...cameraActions, ...animationActions]; // basic action
      const modifiedActions = allActions.map((action) => {
        return {
          ...action,
          action: () => {
            try {
              this.$refs.controlPanel.activeShortcut(action.actionId);
              const actionId = action.actionId;
              const instructionId = `action-only--${actionId.replace("animation--", "")}`;
              this.executeInstruction(instructionId);
            } catch (error) {
              console.error(error);
            }
          },
        };
      });

      // VUE AREA
      this._createButtons(modifiedActions); // Change Exposure 처럼
      // BELOW CODE IS FOR KEYDOWN EVENT
      EventExposure.registerToWindow(modifiedActions); // camera button이 아니라 actions로 부터 가져와야 한다.
    },
    _createButtons(actions) {
      // CAMERA BUTTONS
      this.cameraButtons = actions
        .filter(({ actionId }) => actionId.startsWith("camera"))
        .map(({ actionId, action, shortcutKey }) => {
          return {
            actionId,
            label: actionIdToLabel(actionId),
            onClick: () => action(),
            shortcutKey,
          };
        });
      // ANIMATION BUTTONS
      this.animationButtons = actions
        .filter(({ actionId }) => actionId.startsWith("animation"))
        .map(({ actionId, action, shortcutKey }) => {
          return {
            actionId,
            label: actionIdToLabel(actionId),
            onClick: () => action(),
            shortcutKey,
          };
        });
    },
  },
};
</script>

<style scoped>
.local-body {
  margin: 0 !important;
  padding: 0 !important;
  position: relative;
  height: 100vh;
  overflow: hidden;
}

/* 펫 무대: 수평 중앙 + 하단에서 30vh 위. 크기=펫 박스(500). 프로필 absolute 기준. */
/* 무대+컨트롤 그룹 wrapper = positioning 기준(containing block).
   내부 pet-stage / home-control-panel 은 이 wrapper 기준으로 자기 자리만 잡음
   → 그룹 전체 위치를 옮기려면 여기(inset/transform)만 조절하면 된다. */
.home-stage-group {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center; /* 가로 가운데 */
  /* 위쪽 정렬 (justify-content: flex-start 기본). 그룹 전체 위치/여백은 여기서 조절 */
  gap: 16px;
  padding-top: 24px;
}
.pet-stage {
  position: relative; /* 자식(profile-area) absolute 기준 유지. 배치는 wrapper flow가 담당 */
  width: 400px;
  height: 400px;
}
.pet-box {
  width: 100%;
  height: 100%;
  border-radius: 40px;
  overflow: hidden;
}

/* 일단 이렇게 처리하고 넘어가자 버튼 글자 색상이 검정색으로 보임 */
>>> .keypad .v-btn__content {
  color: whitesmoke;
}

/* 펫 정보 다이얼로그 카드 — 라운드 + 헤더 그라데이션 클리핑 */
.pet-info-card {
  border-radius: 22px !important;
  overflow: hidden;
}

/* 홈 펫 박스 좌측 위 아이콘들 (프로필 · 능력치) */
.profile-area {
  position: absolute;
  top: -6px;
  left: -6px;
  z-index: 4;
  display: flex;
  gap: 8px;
}
.profile-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  font-size: 26px;
  color: #fff;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  border: 2px solid rgba(255, 255, 255, 0.7);
  transition: transform 0.12s, box-shadow 0.12s;
}
.profile-avatar:hover {
  transform: translateY(-2px);
}
.profile-avatar.active {
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.6);
}

.model__foreground {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0px;
  left: 0px;
  z-index: 1;
  pointer-events: none;
}

.model__background {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0px;
  left: 0px;
  z-index: -1;
}
</style>
